import { useEffect, useState } from "react";
import { Download, MessageSquareLock } from "lucide-react";
import { ChatMessage } from "@/components/ChatMessage";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Input";
import { download } from "@/lib/api";
import { LABELS, useLabel } from "@/lib/admin";
import { fmtDate, type AdminLabel, type BriefDetail } from "@/lib/briefs";
import { useChatHistory } from "@/lib/chat";

/**
 * What stands in for the chat when the reader is not the owner: versions,
 * the label and remark for admins, and for the superadmin an explicit,
 * audited "Open chat" that loads the transcript read-only.
 */
export function ReviewPane({ detail }: { detail: BriefDetail }) {
  const { brief, versions, access, sections } = detail;
  const [chatOpen, setChatOpen] = useState(false);
  const transcript = useChatHistory(chatOpen ? brief.id : "");

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="border-border border-b px-4 py-3 lg:px-6">
        <h2 className="text-sm font-semibold">
          {access.as === "superadmin" ? "Superadmin view" : "Review"}
        </h2>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {detail.viewing === "version"
            ? `Showing version ${detail.version} as submitted. The author may have edited since.`
            : brief.submissionCount > 0
              ? "Showing the live brief, including edits since the last submission."
              : "A draft. The author has not submitted it."}
        </p>
      </div>

      <div className="space-y-5 px-4 py-4 lg:px-6">
        <section>
          <h3 className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">Versions</h3>
          {versions.length === 0 ? (
            <p className="text-muted-foreground text-sm">None yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {versions.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-2">
                  <span className="min-w-0">
                    v{v.version} · {fmtDate(v.submittedAt)}
                    {v.notifiedAt ? "" : <span className="text-muted-foreground"> · email pending</span>}
                  </span>
                  <span className="flex shrink-0 items-center">
                    <Button variant="ghost" size="sm" onClick={() => void download(`/briefs/${brief.id}/versions/${v.version}/pdf`, `Solution-Brief-v${v.version}.pdf`)}>
                      <Download size={14} /> PDF
                    </Button>
                    {v.fileCount > 0 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        title={`The PDF and ${v.fileCount} file${v.fileCount === 1 ? "" : "s"}, as a zip`}
                        onClick={() => void download(`/briefs/${brief.id}/versions/${v.version}/bundle`, bundleName(brief.title, v.version))}
                      >
                        <Download size={14} /> All files
                      </Button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {access.canLabel ? (
          <section>
            <h3 className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">Label and remark</h3>
            <LabelControls detail={detail} />
          </section>
        ) : null}

        {access.canReadTranscript ? (
          <section>
            <h3 className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">Chat transcript</h3>
            {!chatOpen ? (
              <div>
                <p className="text-muted-foreground mb-2 text-xs">
                  The chat belongs to the author. Opening it is recorded in the audit log.
                </p>
                <Button variant="outline" size="sm" onClick={() => setChatOpen(true)}>
                  <MessageSquareLock size={14} /> Open chat
                </Button>
              </div>
            ) : transcript.isPending ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : transcript.isError ? (
              <p className="text-destructive text-sm">Could not load the transcript.</p>
            ) : transcript.data.messages.length === 0 ? (
              <p className="text-muted-foreground text-sm">No messages.</p>
            ) : (
              <div className="space-y-3">
                {transcript.data.messages.map((m) => (
                  <ChatMessage key={m.id} message={m} sections={sections} streaming={false} />
                ))}
              </div>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** Matches the name the API gives the bundle: the title's words, then the version. */
function bundleName(title: string, version: number): string {
  const slug = title.replace(/[^\w\s-]+/g, "").trim().replace(/\s+/g, "-").slice(0, 60) || "brief";
  return `${slug}-v${version}.zip`;
}

/**
 * The label and remark an admin sets on a submitted brief. Also shown above the
 * chat when the admin is the brief's owner, since they see the owner's screen.
 */
export function LabelControls({ detail }: { detail: BriefDetail }) {
  const { brief } = detail;
  const label = useLabel(brief.id);
  const [chosen, setChosen] = useState<AdminLabel | "">(brief.adminLabel ?? "");
  const [remark, setRemark] = useState(brief.adminRemark ?? "");

  useEffect(() => {
    setChosen(brief.adminLabel ?? "");
    setRemark(brief.adminRemark ?? "");
  }, [brief.adminLabel, brief.adminRemark]);

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {LABELS.map((l) => (
          <Button key={l} size="sm" variant={chosen === l ? "primary" : "outline"} onClick={() => setChosen(chosen === l ? "" : l)}>
            {l}
          </Button>
        ))}
      </div>
      <Textarea value={remark} onChange={(e) => setRemark(e.target.value)} maxLength={2000} placeholder="A remark for the author (they are told in-app)." />
      <div className="mt-2 flex items-center justify-between">
        <span className="text-muted-foreground text-xs">{brief.adminAt ? `Last changed ${fmtDate(brief.adminAt)}` : ""}</span>
        <Button
          size="sm"
          disabled={label.isPending}
          onClick={() => label.mutate({ adminLabel: chosen || null, adminRemark: remark.trim() || null })}
        >
          {label.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
      {label.isError ? <p className="text-destructive mt-1 text-xs">{(label.error as Error).message}</p> : null}
    </div>
  );
}
